<template>
  <div class="min-h-screen text-white px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
    <div class="mx-auto max-w-5xl">
      <div class="flex justify-center animate__animated animate__fadeIn">
        <img
          src="profile_pic.jpg"
          class="h-40 w-40 rounded-full object-cover shadow-2xl sm:h-52 sm:w-52 md:h-64 md:w-64"
          alt="Daffa's Profile Picture"
        />
      </div>

      <!-- Section name and tagline -->
      <div class="pt-6 text-center animate__animated animate__fadeIn">
        <h1 class="text-3xl font-medium sm:text-4xl md:text-5xl">
          Muhammad Daffa Razan
        </h1>
        <p
          class="mt-3 text-sm font-medium text-slate-200 sm:text-base md:text-lg"
        >
          Flutter Mobile Engineer
        </p>
      </div>
      <!-- End Section name and tagline -->

      <div id="arrow" class="pt-10 text-center">
        <a href="#introduction">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="mx-auto h-12 w-12 animate-bounce text-green-500 sm:h-14 sm:w-14"
            fill="currentColor"
          >
            <path
              d="M13.0001 16.1716L18.3641 10.8076L19.7783 12.2218L12.0001 20L4.22192 12.2218L5.63614 10.8076L11.0001 16.1716V4H13.0001V16.1716Z"
            ></path>
          </svg>
        </a>
      </div>

      <!-- Section about -->
      <section id="introduction" class="pt-14 sm:pt-16">
        <h2
          class="text-green-500 text-3xl font-semibold sm:text-4xl md:text-5xl pb-6"
          data-aos="fade-up"
        >
          Intro
        </h2>
        <div class="space-y-5 text-slate-200">
          <p
            class="text-sm leading-7 sm:text-base md:text-lg"
            data-aos="fade-up"
          >
            Hello everyone! I'm
            <span class="text-green-500">Muhammad Daffa Razan</span>, a
            passionate Mobile Developer with 3 years of hands-on experience
            crafting engaging and efficient Flutter apps. Over the past 4+ years
            in mobile development, I've honed my skills, focusing primarily on
            Flutter. I've built apps using Clean Architecture, making use of
            BLoC, Provider, and GetX to ensure clean, scalable code and
            top-notch performance.
          </p>
          <p
            class="text-sm leading-7 sm:text-base md:text-lg"
            data-aos="fade-up"
          >
            I’ve successfully launched apps on both the Play Store and App
            Store, reaching users around the globe. Alongside mobile
            development, I have an advanced certificate in Kotlin and enjoy
            diving into iOS development during my spare time. My expertise also
            extends to web development, where I've worked with frameworks like
            Laravel, CodeIgniter, React, and Vue.
          </p>
          <p
            class="text-sm leading-7 sm:text-base md:text-lg"
            data-aos="fade-up"
          >
            I'm always excited to connect with like-minded professionals, so
            feel free to reach out—whether it’s to collaborate or explore
            opportunities together!
          </p>
        </div>
      </section>
      <!-- End Section about -->

      <!-- Download CV Section -->
      <div class="mt-10 mb-12 flex justify-center" data-aos="fade-up">
        <a
          href="https://docs.google.com/document/d/1QXmz2ySDXVbiTL31SJhszdX6dmjqyCYNNDtvQNfjTig/edit?usp=sharing"
          target="_blank"
          class="inline-flex w-full max-w-xs items-center justify-center rounded-full bg-green-500 px-6 py-3 text-center text-sm font-semibold text-white transition duration-300 hover:bg-green-700 hover:scale-105 sm:w-auto sm:text-base"
          type="submit"
        >
          See CV
        </a>
      </div>

      <!-- Section expertise -->
      <section class="space-y-12">
        <div>
          <h2
            class="text-green-500 text-3xl font-semibold sm:text-4xl md:text-5xl pb-5"
            data-aos="fade-up"
          >
            Expertise
          </h2>
        </div>

        <div class="space-y-10">
          <div>
            <h3
              class="text-green-500 text-2xl font-semibold sm:text-3xl pb-4"
              data-aos="fade-up"
            >
              Web Dev
            </h3>
            <div
              class="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6"
              data-aos="flip-left"
            >
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/html.svg"
                alt="html logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/css.svg"
                alt="css logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/js.svg"
                alt="js logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/bootstrap.svg"
                alt="bootstrap logo"
              />
            </div>
            <div
              class="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6"
              data-aos="flip-left"
            >
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/laravel.svg"
                alt="laravel logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/ci.svg"
                alt="code igniter logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/django.svg"
                alt="django logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/vue.svg"
                alt="vue logo"
              />
            </div>
            <div
              class="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6"
              data-aos="flip-left"
            >
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/nextjs.svg"
                alt="next js logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/tailwind.svg"
                alt="tailwind css logo"
              />
            </div>
          </div>

          <div>
            <h3
              class="text-green-500 text-2xl font-semibold sm:text-3xl pb-4"
              data-aos="fade-up"
            >
              Mobile Dev
            </h3>
            <div
              class="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6"
              data-aos="flip-left"
            >
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/kotlin.png"
                alt="kotlin logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/flutter.png"
                alt="flutter logo"
              />
            </div>
          </div>

          <div>
            <h3
              class="text-green-500 text-2xl font-semibold sm:text-3xl pb-4"
              data-aos="fade-up"
            >
              Design
            </h3>
            <div
              class="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6"
              data-aos="flip-left"
            >
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/ai.svg"
                alt="adobe illustrator logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/ps.svg"
                alt="adobe photoshop logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/xd.svg"
                alt="adobe xd logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/figma.svg"
                alt="figma logo"
              />
            </div>
          </div>

          <div>
            <h3
              class="text-green-500 text-2xl font-semibold sm:text-3xl pb-4"
              data-aos="fade-up"
            >
              Programming Language
            </h3>
            <div
              class="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6"
              data-aos="flip-left"
            >
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/python.svg"
                alt="python logo"
              />
              <img
                class="mx-auto h-16 w-auto sm:h-20"
                src="expertise/java.svg"
                alt="java logo"
              />
            </div>
          </div>
        </div>
      </section>
      <!-- End Section expertise -->
    </div>
  </div>
</template>

<script>
export default {
  name: "HomePage",
  components: {},
};
</script>
